import * as React from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { CloudDownloadFill } from "react-bootstrap-icons";
import Container from "react-bootstrap/Container";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";

import { AppGuest } from "../types";
import { AuthContext } from "../context";

const OpenRsvp = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<
    (AppGuest & { access_token: string }) | null
  >(null);

  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();

      void (async () => {
        setLoading(true);

        try {
          const response = await window.fetch("/api/guests", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: nameRef.current!.value,
              email: emailRef.current!.value,
              // eslint-disable-next-line camelcase
              access_token: tokenRef.current!.value,
            }),
          });
          if (response.status === 200) {
            setUser(
              (await response.json()) as AppGuest & { access_token: string }
            );
          } else {
            setError(((await response.json()) as { error: string }).error);
          }
        } catch (error) {
          setError(String(error));
        } finally {
          setLoading(false);
        }
      })();
    },
    [setUser]
  );

  const tokenRef = React.useRef<HTMLInputElement>(null);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const nameRef = React.useRef<HTMLInputElement>(null);

  const { fetch, setAuth } = React.useContext(AuthContext);

  let initialAccessCode = "";
  const [searchParams] = useSearchParams();
  if (searchParams.has("access-token")) {
    initialAccessCode = searchParams.get("access-token")!;
  }

  const navigate = useNavigate();

  const handleRsvp = React.useCallback(() => {
    setAuth({
      email: user!.email,
      accessToken: user!.access_token,
    });
    navigate("/rsvp");
  }, [navigate, setAuth, user]);

  if (fetch != null) {
    return <Navigate replace to="/rsvp" />;
  }

  if (user != null) {
    return (
      <Container className="col-12 col-sm-6">
        <p className="text-light lead">
          Your account has been created with the following details. Before
          clicking RSVP, please save them for future reference:
        </p>
        <p className="text-light fs-5 d-flex justify-content-between">
          <dl className="mb-0">
            <dt className="fw-semibold">Email address</dt>
            <dd>{user.email}</dd>
            <dt className="fw-semibold">Access code</dt>
            <dd>{user.access_token}</dd>
          </dl>
          <div>
            <Button
              as="a"
              download="sushain-snigdha-login-info.txt"
              href={
                "data:text/plain;base64," +
                btoa(`email: ${user.email}\naccess-code: ${user.access_token}`)
              }
              size="sm"
            >
              <CloudDownloadFill /> Download
            </Button>
          </div>
        </p>
        <p>
          <Button onClick={handleRsvp}>RSVP</Button>
        </p>
      </Container>
    );
  }

  return (
    <Container className="col-12 col-sm-6">
      <p className="text-light lead">
        Please start by creating an account to view event details and RSVP.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={handleSubmit}>
        <FloatingLabel className="mb-3" controlId="name" label="Name">
          <Form.Control placeholder="Name" ref={nameRef} required />
        </FloatingLabel>
        <FloatingLabel
          className="mb-3"
          controlId="email-address"
          label="Email address"
        >
          <Form.Control
            placeholder="name@example.com"
            ref={emailRef}
            required
            type="email"
          />
        </FloatingLabel>
        <FloatingLabel
          className="mb-4"
          controlId="access-code"
          label="Access code"
        >
          <Form.Control
            defaultValue={initialAccessCode}
            placeholder="access-code"
            ref={tokenRef}
            required
            type="password"
          />
        </FloatingLabel>
        <Button
          className="w-100"
          disabled={loading}
          type="submit"
          variant="primary"
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            "Create account"
          )}
        </Button>
      </Form>
    </Container>
  );
};

export default OpenRsvp;
